import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import DocPdf from '../Pdf/DocPdf';
import { OrdersFetchAPI } from "../../api/Orders";
import { RenderIf } from '../../utils/RenderIf';
import { buildCloudinaryImageUrl } from '../../utils/CloudinaryImageUrl';
/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .iom-
───────────────────────────────────────────────────────── */
const IOM_CSS = `
@keyframes iom-fade  { from{opacity:0}                                    to{opacity:1} }
@keyframes iom-slide { from{opacity:0;transform:translateY(18px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes iom-up    { from{opacity:0;transform:translateY(8px)}           to{opacity:1;transform:translateY(0)} }

/* overlay */
.iom-overlay {
  position: fixed; inset: 0; z-index: 1080;
  background: rgba(0,0,0,.44);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
  animation: iom-fade .2s ease both;
}

/* panel */
.iom-panel {
  width: 100%; max-width: 580px;
  max-height: calc(100vh - 2rem);
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.08));
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(0,0,0,.18), 0 6px 16px rgba(0,0,0,.08);
  display: flex; flex-direction: column;
  overflow: hidden;
  animation: iom-slide .28s cubic-bezier(.34,1.56,.64,1) both;
}
[data-theme="dark"] .iom-panel {
  box-shadow: 0 24px 64px rgba(0,0,0,.7), 0 6px 16px rgba(0,0,0,.5);
}

/* header */
.iom-header {
  display: flex; align-items: center; gap: .65rem;
  padding: 1rem 1.2rem .9rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
  flex-shrink: 0;
}
.iom-header-icon {
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, --color-primary);
  display: grid; place-items: center; flex-shrink: 0;
}
.iom-header-info { flex: 1; min-width: 0; }
.iom-header-title {
  font-size: .93rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.iom-header-code {
  font-size: .7rem; color: var(--text-3, #B4B4B4);
  font-weight: 400; margin-top: 1px;
}
.iom-close {
  width: 30px; height: 30px; border-radius: 50%;
  border: 1.5px solid var(--border, rgba(0,0,0,.08));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  display: grid; place-items: center;
  cursor: pointer; transition: all .15s; flex-shrink: 0;
}
.iom-close:hover { border-color: #EF4444; color: #EF4444; background: #FEE2E2; }
[data-theme="dark"] .iom-close:hover { background: #2A0A0A; }

/* scroll body */
.iom-body {
  flex: 1; overflow-y: auto; overflow-x: hidden;
  padding: 1rem 1.2rem;
  scrollbar-width: thin;
  scrollbar-color: var(--border, rgba(0,0,0,.07)) transparent;
  display: flex; flex-direction: column; gap: 1rem;
}
.iom-body::-webkit-scrollbar { width: 4px; }
.iom-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,.08); border-radius: 2px; }

/* section label */
.iom-section-label {
  font-size: .68rem; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  color: var(--text-3, #B4B4B4);
  margin-bottom: .6rem;
  padding-bottom: .35rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}

/* product card */
.iom-product {
  display: flex; align-items: flex-start; gap: .75rem;
  padding: .7rem .8rem;
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 12px;
  background: var(--bg, #fff);
  transition: box-shadow .14s;
  animation: iom-up .2s ease both;
}
.iom-product:hover { box-shadow: 0 3px 12px rgba(0,0,0,.07); }
.iom-product + .iom-product { margin-top: .45rem; }

.iom-product-img {
  width: 52px; height: 52px; border-radius: 9px;
  object-fit: cover; flex-shrink: 0;
  border: 1px solid var(--border, rgba(0,0,0,.07));
  background: var(--bg-2, rgba(0,0,0,.04));
}
.iom-product-body { flex: 1; min-width: 0; }
.iom-product-top {
  display: flex; align-items: flex-start;
  justify-content: space-between; gap: .4rem; margin-bottom: .2rem;
}
.iom-product-name  { font-size: .83rem; font-weight: 600; color: var(--text, #0A0A0A); line-height: 1.3; }
.iom-product-price { font-size: .83rem; font-weight: 700; color: var(--brand, --color-primary); flex-shrink: 0; }

/* segments / details */
.iom-details-toggle {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: .72rem; font-weight: 500; color: var(--text-3, #B4B4B4);
  cursor: pointer; margin-top: .25rem;
  background: none; border: none; padding: 0;
  font-family: var(--font-sans, -apple-system, sans-serif);
  transition: color .14s;
}
.iom-details-toggle:hover { color: var(--brand, --color-primary); }

.iom-segments { margin-top: .4rem; display: flex; flex-direction: column; gap: .2rem; }
.iom-segment {
  display: flex; align-items: center; justify-content: space-between;
  padding: .28rem .6rem;
  border-radius: 7px;
  background: var(--bg-2, rgba(0,0,0,.035));
  font-size: .73rem;
}
.iom-segment-name  { color: var(--text-2, #6B6B6B); }
.iom-segment-price { color: var(--brand, --color-primary); font-weight: 600; }

/* summary */
.iom-summary {
  background: var(--bg-2, rgba(0,0,0,.035));
  border-radius: 12px; padding: .85rem 1rem;
}
.iom-sum-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: .81rem; color: var(--text-2, #6B6B6B); margin-bottom: .22rem;
}
.iom-sum-row:last-child { margin-bottom: 0; }
.iom-sum-divider { height: 1px; background: var(--border, rgba(0,0,0,.07)); margin: .45rem 0; }
.iom-sum-total { font-size: .92rem; font-weight: 700; }
.iom-sum-total .iom-sum-label-s { color: var(--text, #0A0A0A); }
.iom-sum-total .iom-sum-val-s   { color: var(--brand, --color-primary); font-size: 1rem; }
.iom-sum-label-s { }
.iom-sum-val-s   { font-weight: 600; color: var(--text, #0A0A0A); }

/* payment badge */
.iom-payment-badge {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .2rem .6rem;
  border-radius: 999px;
  background: rgba(34,197,94,.10); color: #15803D;
  font-size: .72rem; font-weight: 600;
}
[data-theme="dark"] .iom-payment-badge { background: rgba(34,197,94,.15); color: #4ADE80; }

/* footer */
.iom-footer {
  flex-shrink: 0;
  padding: .85rem 1.2rem 1rem;
  border-top: 1px solid var(--border, rgba(0,0,0,.07));
  display: flex; flex-direction: column; gap: .5rem;
}
.iom-footer-row { display: flex; gap: .5rem; }

/* buttons */
.iom-btn-ghost {
  flex: 1;
  padding: .65rem .85rem;
  border-radius: 11px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .8rem; font-weight: 500;
  cursor: pointer; transition: all .14s;
  display: flex; align-items: center; justify-content: center; gap: .38rem;
}
.iom-btn-ghost:hover { border-color: var(--text-2, #6B6B6B); color: var(--text, #0A0A0A); }

.iom-btn-danger {
  flex: 1;
  padding: .65rem .85rem;
  border-radius: 11px;
  border: 1.5px solid rgba(239,68,68,.2);
  background: rgba(239,68,68,.07);
  color: #EF4444;
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .8rem; font-weight: 500;
  cursor: pointer; transition: all .14s;
  display: flex; align-items: center; justify-content: center; gap: .38rem;
}
.iom-btn-danger:hover { background: #FEE2E2; border-color: #EF4444; }

.iom-btn-primary {
  width: 100%;
  padding: .72rem 1rem;
  border-radius: 12px; border: none;
  background: var(--brand, --color-primary); color: #fff;
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .86rem; font-weight: 600;
  cursor: pointer; transition: background .14s, transform .15s, box-shadow .15s;
  display: flex; align-items: center; justify-content: center; gap: .42rem;
}
.iom-btn-primary:hover {
  background: var(--brand-hover, #D4521A);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(242,101,34,.35);
}
.iom-btn-primary:active { transform: translateY(0); box-shadow: none; }

/* pdf link wrapper */
.iom-pdf-link { flex: 1; text-decoration: none; }
.iom-pdf-link button { width: 100%; }

/* responsive */
@media(max-width: 480px) {
  .iom-footer-row { flex-direction: column; }
  .iom-pdf-link { width: 100%; }
}
`;

/* ── ICONS ── */
const IcoReceipt = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>;
const IcoX = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const IcoBack = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 18 9 12 15 6" /></svg>;
const IcoCard = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>;
const IcoPay = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>;
const IcoTrash = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>;
const IcoDown = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></svg>;
const IcoChevron = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>;
const IcoCheck = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>;

/* ─────────────────────────────────────────────────────────
   COMPONENT — lógica 100% original, nuevo shell visual
───────────────────────────────────────────────────────── */
const InfoOrdersModal = ({ modalShow, setModalShow, selectedOrder }) => {

  /* ── LÓGICA ORIGINAL INTACTA ── */
  const [branchOfficeData, setBranchofficeData] = useState({
    "ruc": "", "dv": "", "name": "",
    "ruc_type": "", "direction": "", "dgi_name": "",
  });
  const navigate = useNavigate();

  const getTemporalOrderById = async () => {
    try {
      let token = undefined;
      const response = await OrdersFetchAPI.getTempOrderById(selectedOrder._id, token);
      let dataOrder = response.data.pre_order;
      console.log(dataOrder);
      navigate("/cart/order/" + dataOrder.code + "/" + dataOrder._id);
    } catch (err) {
      console.log(err);
    }
  };

  const cardProduct = (product, index) => {
    // La función cardProduct original se conserva internamente
    // pero el render usa el nuevo shell visual (ver renderProduct)
    return null;
  };
  /* ── FIN LÓGICA ORIGINAL ── */

  /* inject CSS once */
  useEffect(() => {
    const id = "iom-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = IOM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  /* ESC to close */
  useEffect(() => {
    if (!modalShow) return;
    const fn = (e) => { if (e.key === "Escape") setModalShow(false); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [modalShow, setModalShow]);

  /* body scroll lock */
  useEffect(() => {
    document.body.style.overflow = modalShow ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [modalShow]);

  /* ── render product con nuevo diseño ── */
  const [openDetails, setOpenDetails] = useState({});
  const toggleDetails = (idx) => setOpenDetails(p => ({ ...p, [idx]: !p[idx] }));

  const renderProduct = (product, index) => (
    <div className="iom-product" key={product._id || index}
      style={{ animationDelay: `${index * 0.05}s` }}>

      <img
        className="iom-product-img"
        src={buildCloudinaryImageUrl({ url: product.img, width: 300, quality: "auto", type: "image" }) || undefined}
        alt={product.name}
        onError={e => { e.target.style.opacity = 0; }}
      />

      <div className="iom-product-body">

        <div className="iom-product-top">
          <span className="iom-product-name">{product.name}</span>
          <span className="iom-product-price">${product.price.toFixed(2)}</span>
        </div>

        {/* cantidad */}
        <div style={{ fontSize: '.7rem', color: '#999' }}>
          x{product.amount}
        </div>

        {/* descuento */}
        {product.discount > 0 && (
          <div style={{ fontSize: '.7rem', color: '#EF4444', fontWeight: 600 }}>
            -${product.discount.toFixed(2)} descuento
          </div>
        )}

        {/* nota */}
        {product.note && (
          <div style={{ fontSize: '.7rem', color: '#666', marginTop: '2px' }}>
            📝 {product.note}
          </div>
        )}

        {/* segments */}
        {product.segments?.length > 0 && (
          <>
            <button className="iom-details-toggle" onClick={() => toggleDetails(index)}>
              <IcoChevron />
              {openDetails[index] ? 'Ocultar detalles' : `${product.segments.length} opciones`}
            </button>

            {openDetails[index] && (
              <div className="iom-segments">
                {product.segments.map((seg, si) => (
                  <div className="iom-segment" key={si}>
                    <span className="iom-segment-name">{seg.item.name}</span>
                    {parseFloat(seg.item.price) > 0 && (
                      <span className="iom-segment-price">
                        +${parseFloat(seg.item.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );

  if (!modalShow) return null;

  /* ── RENDER ── */
  return createPortal(
    <div
      className="iom-overlay"
      onClick={e => { if (e.target === e.currentTarget) setModalShow(false); }}
    >
      <div className="iom-panel" role="dialog" aria-modal="true" aria-label="Detalle de orden">

        {/* HEADER */}
        <div className="iom-header">
          <div className="iom-header-icon"><IcoReceipt /></div>
          <div className="iom-header-info">
            <div className="iom-header-title">{selectedOrder.is_quote_order ? 'Cotización' : 'Orden'} #{selectedOrder.transaction_code}</div>
            {selectedOrder.transaction_code && (
              <div className="iom-header-code">Transacción: {selectedOrder.transaction_code}</div>
            )}
          </div>
          <button className="iom-close" onClick={() => setModalShow(false)} aria-label="Cerrar">
            <IcoX />
          </button>
        </div>

        {/* BODY */}
        <div className="iom-body">

          {/* estado / historial */}
          {selectedOrder.status_update_history?.length > 0 && (
            <div>
              <div className="iom-section-label">Estado de la orden</div>

              {selectedOrder.status_update_history.map((h, i) => (
                <div key={i} style={{
                  fontSize: '.75rem',
                  marginBottom: '4px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>{h.name || h.status}</span>
                  <span style={{ color: '#999' }}>
                    {new Date(h.createdAt).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* delivery */}
          {selectedOrder.delivery && (
            <div>
              <div className="iom-section-label">Entrega</div>

              <div style={{ fontSize: '.8rem', color: '#444' }}>
                <div><b>Tipo:</b> {selectedOrder.delivery.platform || "N/A"}</div>
                <div><b>Orden externa:</b> {selectedOrder.delivery.order_id || "-"}</div>
                <div><b>Costo delivery:</b> ${selectedOrder.delivery.value?.toFixed(2) || "0.00"}</div>
              </div>
            </div>
          )}
          {/* voucher */}
          {selectedOrder.voucher_response && (
            <div>
              <div className="iom-section-label">Voucher</div>

              <div style={{
                fontSize: '.7rem',
                background: '#f5f5f5',
                padding: '8px',
                borderRadius: '6px',
                wordBreak: 'break-all'
              }}>
                {selectedOrder.voucher_response}
              </div>
            </div>
          )}
          {/* productos */}
          <div>
            <div className="iom-section-label">Productos</div>
            {selectedOrder.groups.map((product, index) => renderProduct(product, index))}
          </div>

          {/* resumen */}
          <div>
            <div className="iom-section-label">Resumen de pago</div>
            <div className="iom-summary">
              {selectedOrder.subtotal !== undefined && (
                <div className="iom-sum-row">
                  <span className="iom-sum-label-s">Subtotal</span>
                  <span className="iom-sum-val-s">${selectedOrder.subtotal.toFixed(2)}</span>
                </div>
              )}
              {selectedOrder.tax > 0 && (
                <div className="iom-sum-row">
                  <span className="iom-sum-label-s">ITBMS</span>
                  <span className="iom-sum-val-s">${selectedOrder.tax.toFixed(2)}</span>
                </div>
              )}
              {selectedOrder.discount > 0 && (
                <div className="iom-sum-row">
                  <span className="iom-sum-label-s">Descuento</span>
                  <span style={{ fontWeight: 600, color: '#EF4444' }}>−${selectedOrder.discount.toFixed(2)}</span>
                </div>
              )}
              {selectedOrder.payment && (
                <div className="iom-sum-row">
                  <span className="iom-sum-label-s">Método de pago</span>
                  <span className="iom-payment-badge"><IcoCheck /> Visa</span>
                </div>
              )}
              <div className="iom-sum-divider" />
              <div className="iom-sum-row iom-sum-total">
                <span className="iom-sum-label-s">Total</span>
                <span className="iom-sum-val-s" style={{ color: 'var(--brand, --color-primary)', fontSize: '1rem' }}>
                  ${selectedOrder.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

        </div>
        <RenderIf isTrue={selectedOrder.is_quote_order === true}>
          {/* FOOTER — acciones */}
          <div className="iom-footer">

            {/* fila 1: descartar + descargar PDF */}
            <div className="iom-footer-row">
              <button
                className="iom-btn-danger"
                onClick={() => navigate("/cart/order/" + selectedOrder.code + "/" + selectedOrder._id)}
              >
                <IcoTrash /> Descartar
              </button>

              <PDFDownloadLink
                className="iom-pdf-link"
                document={
                  <DocPdf
                    invoiceQR={selectedOrder.dgi_qr || "0"}
                    doc_type={"price"}
                    invoiceDate={selectedOrder.createdAt}
                    cufe={selectedOrder.cufe || "0"}
                    auth_protocol={selectedOrder.auth_protocol ? selectedOrder.auth_protocol : "00000000"}
                    receptor={{
                      name: selectedOrder.client_name,
                      ruc: selectedOrder.client_ruc,
                      direction: "Panama",
                      dv: selectedOrder.client_dv,
                    }}
                    emitter={branchOfficeData}
                    products={selectedOrder.groups}
                    invoiceSubtotal={selectedOrder.subtotal}
                    totalTaxes={selectedOrder.taxes || []}
                    totalCashReceived={selectedOrder.total}
                    invoiceTotal={selectedOrder.total}
                    invoiceTotalItbms={selectedOrder.tax}
                    paymentMethod={selectedOrder.paymenthod}
                  />
                }
              >
                <button className="iom-btn-ghost">
                  <IcoDown /> Descargar PDF
                </button>
              </PDFDownloadLink>
            </div>

            {/* fila 2: proceder al pago */}
            <button className="iom-btn-primary" onClick={() => getTemporalOrderById()}>
              <IcoPay /> Proceder al Pago
            </button>


          </div>
        </RenderIf>

      </div>
    </div>,
    document.body
  );
};

export default InfoOrdersModal;